import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Panel } from "@crashx/ui";
import { selectBets, useRoundStore } from "../../state/roundStore.js";
import { BetTable } from "./BetTable.js";
import { TopFilters } from "./TopFilters.js";
import { useBetHistory, type BetTab, type BetPeriod, type BetSort } from "./useBetHistory.js";

const TABS: BetTab[] = ["all", "my", "top"];
export function BetList() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<BetTab>("all");
  const [page, setPage] = useState(0);
  useEffect(
    () =>
      useRoundStore.subscribe((current, previous) => {
        if (current.round?.roundId !== previous.round?.roundId) setPage(0);
      }),
    [],
  );
  const [period, setPeriod] = useState<BetPeriod>("day");
  const [sort, setSort] = useState<BetSort>("win");
  const defaultCurrency = useRoundStore((state) => state.config?.currencies[0]?.code ?? "FUN");
  const [selection, setSelection] = useState<string | null>(null);
  const currency = selection ?? defaultCurrency;
  const bets = useRoundStore(selectBets);
  const history = useBetHistory(tab, page, period, sort, currency);
  const rows = tab === "all" ? bets.slice(page * 20, (page + 1) * 20) : history.rows;
  const select = (next: BetTab) => {
    setTab(next);
    setPage(0);
  };
  return (
    <Panel className="cx-bet-list">
      <div className="cx-bet-list-tabs" role="tablist" aria-label={t("bets.title")}>
        {TABS.map((id, index) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`bets-tab-${id}`}
            aria-controls="bets-results"
            aria-selected={tab === id}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => select(id)}
            className="cx-btn cx-btn--ghost"
            style={tab === id ? { color: "var(--cx-color-text)" } : undefined}
            onKeyDown={(event) => {
              const nextIndex =
                event.key === "ArrowRight"
                  ? (index + 1) % TABS.length
                  : event.key === "ArrowLeft"
                    ? (index + TABS.length - 1) % TABS.length
                    : event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? TABS.length - 1
                        : -1;
              const next = TABS[nextIndex];
              if (next) {
                event.preventDefault();
                select(next);
                document.getElementById(`bets-tab-${next}`)?.focus();
              }
            }}
          >
            {t("bets." + id)}
          </button>
        ))}
      </div>
      <div className="cx-bet-list-total">
        <span>{t("bets.total")}</span>
        <strong>{t("bets.count", { count: rows.length })}</strong>
      </div>
      {tab === "top" ? (
        <>
          <TopFilters
            period={period}
            sort={sort}
            currency={currency}
            onPeriod={setPeriod}
            onSort={setSort}
            onCurrency={setSelection}
          />
          <p className="m-0 text-xs text-muted">{t("bets.topHint")}</p>
        </>
      ) : null}
      <div
        className="cx-bet-list-results"
        role="tabpanel"
        id="bets-results"
        aria-labelledby={`bets-tab-${tab}`}
      >
        {tab !== "all" && history.error ? (
          <div role="alert">
            <p>{t("bets.error")}</p>
            <Button variant="ghost" onClick={history.retry}>
              {t("bets.retry")}
            </Button>
          </div>
        ) : tab !== "all" && history.loading ? (
          <p role="status">{t("app.loading")}</p>
        ) : (
          <BetTable rows={rows} />
        )}
      </div>
      {tab !== "top" ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            disabled={page === 0 || (tab !== "all" && history.loading)}
            onClick={() => setPage((value) => value - 1)}
          >
            {t("bets.newer")}
          </Button>
          <span className="text-xs text-muted">{t("bets.page", { page: page + 1 })}</span>
          <Button
            variant="ghost"
            disabled={
              rows.length < 20 ||
              (tab === "all" && (page + 1) * 20 >= bets.length) ||
              (tab !== "all" && history.loading)
            }
            onClick={() => setPage((value) => value + 1)}
          >
            {t("bets.older")}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}
